import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Building2,
  CheckCircle,
  Pencil,
  X,
  Globe,
  BookOpen,
  Tag,
  Briefcase,
  ChevronRight,
} from 'lucide-react';
import axios from 'axios';

const BASE_ADMIN = '/api/v1/paie/administration';

interface Organisation {
  idOrganisation: string;
  nom: string;
  typeOrganisation: string;
  referentielComptable: string;
  pays: string;
  description?: string;
}

type TypeOrganisation = 'ENTREPRISE' | 'ONG' | 'PROJET_DEVELOPPEMENT' | 'ASSOCIATION' | 'FONDATION' | 'AUTRE';
type ReferentielComptable = 'SYSCOHADA' | 'SYCEBNL';

const LABELS_TYPE: Record<string, string> = {
  ENTREPRISE:           'Entreprise',
  ONG:                  'ONG',
  PROJET_DEVELOPPEMENT: 'Projet de développement',
  ASSOCIATION:          'Association',
  FONDATION:            'Fondation',
  AUTRE:                'Autre',
};

const TYPES_ORGANISATION: { value: TypeOrganisation; label: string }[] = [
  { value: 'ENTREPRISE',           label: 'Entreprise' },
  { value: 'ONG',                  label: 'ONG' },
  { value: 'PROJET_DEVELOPPEMENT', label: 'Projet de développement' },
  { value: 'ASSOCIATION',          label: 'Association' },
  { value: 'FONDATION',            label: 'Fondation' },
  { value: 'AUTRE',                label: 'Autre' },
];

function fetchOrganisation(): Promise<Organisation | null> {
  return axios
    .get(`${BASE_ADMIN}/organisation/moi`)
    .then((r) => r.data?.data ?? null)
    .catch(() => null);
}

function putOrganisation(data: Partial<Organisation>): Promise<Organisation> {
  return axios
    .put(`${BASE_ADMIN}/organisation/moi`, data)
    .then((r) => r.data?.data);
}

export function OrganisationPage() {
  const queryClient = useQueryClient();
  const [modeEdition, setModeEdition] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: org, isLoading } = useQuery({
    queryKey: ['organisation-moi'],
    queryFn: fetchOrganisation,
  });

  const [form, setForm] = useState({
    nom: '',
    pays: 'BJ',
    typeOrganisation: 'ENTREPRISE' as TypeOrganisation,
    referentielComptable: 'SYSCOHADA' as ReferentielComptable,
    description: '',
  });

  useEffect(() => {
    if (org) {
      setForm({
        nom: org.nom ?? '',
        pays: org.pays ?? 'BJ',
        typeOrganisation: (org.typeOrganisation as TypeOrganisation) ?? 'ENTREPRISE',
        referentielComptable: (org.referentielComptable as ReferentielComptable) ?? 'SYSCOHADA',
        description: org.description ?? '',
      });
    }
  }, [org]);

  const mutation = useMutation({
    mutationFn: () => putOrganisation(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organisation-moi'] });
      setSaved(true);
      setModeEdition(false);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const annuler = () => {
    if (org) {
      setForm({
        nom: org.nom ?? '',
        pays: org.pays ?? 'BJ',
        typeOrganisation: (org.typeOrganisation as TypeOrganisation) ?? 'ENTREPRISE',
        referentielComptable: (org.referentielComptable as ReferentielComptable) ?? 'SYSCOHADA',
        description: org.description ?? '',
      });
    }
    setModeEdition(false);
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <div className="h-8 w-48 bg-surface-overlay rounded animate-pulse" />
        <div className="bg-surface border border-border rounded-xl p-6 animate-pulse space-y-4">
          <div className="h-5 bg-surface-overlay rounded w-1/3" />
          <div className="h-4 bg-surface-raised rounded w-1/2" />
          <div className="h-4 bg-surface-raised rounded w-2/5" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* ── En-tête ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-primary-text" />
          <h1 className="text-2xl font-bold text-content">Organisation</h1>
        </div>
        {!modeEdition && org && (
          <button
            onClick={() => setModeEdition(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
                       text-primary-text bg-primary-light hover:bg-primary hover:text-content-inverted
                       rounded-lg transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </button>
        )}
      </div>

      {/* ── Message succès ── */}
      {saved && (
        <div className="flex items-center gap-2 text-sm text-success bg-success-light border border-success rounded-lg px-4 py-3">
          <CheckCircle className="w-4 h-4" />
          Organisation mise à jour avec succès.
        </div>
      )}

      {/* ── Fiche lecture ── */}
      {!modeEdition && org && (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {/* Bandeau nom */}
          <div className="px-6 py-5 bg-primary-light border-b border-border flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-content-inverted" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-content">{org.nom}</h2>
              <p className="text-sm text-content-subtle">{LABELS_TYPE[org.typeOrganisation] ?? org.typeOrganisation}</p>
            </div>
          </div>

          {/* Détails */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-border">
            <div className="px-6 py-4 flex items-start gap-3">
              <Globe className="w-4 h-4 text-content-subtle mt-0.5" />
              <div>
                <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-0.5">Pays</p>
                <p className="text-sm font-medium text-content">{org.pays}</p>
              </div>
            </div>
            <div className="px-6 py-4 flex items-start gap-3">
              <BookOpen className="w-4 h-4 text-content-subtle mt-0.5" />
              <div>
                <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-0.5">Référentiel comptable</p>
                <p className="text-sm font-medium text-content">{org.referentielComptable}</p>
              </div>
            </div>
            <div className="px-6 py-4 flex items-start gap-3">
              <Tag className="w-4 h-4 text-content-subtle mt-0.5" />
              <div>
                <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-0.5">Type</p>
                <p className="text-sm font-medium text-content">{LABELS_TYPE[org.typeOrganisation] ?? org.typeOrganisation}</p>
              </div>
            </div>
            {org.description && (
              <div className="px-6 py-4 sm:col-span-2 flex items-start gap-3">
                <div>
                  <p className="text-xs font-medium text-content-subtle uppercase tracking-wider mb-0.5">Description</p>
                  <p className="text-sm text-content-muted">{org.description}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Formulaire édition ── */}
      {modeEdition && (
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-surface-raised border-b border-border">
            <h2 className="text-base font-semibold text-content">Modifier l'organisation</h2>
            <button onClick={annuler} className="text-content-subtle hover:text-content transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Nom de l'organisation</label>
              <input
                type="text"
                value={form.nom}
                onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))}
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-surface text-content
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Pays</label>
              <input
                type="text"
                value={form.pays}
                onChange={(e) => setForm((f) => ({ ...f, pays: e.target.value }))}
                placeholder="BJ"
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-surface text-content
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Type d'organisation</label>
              <select
                value={form.typeOrganisation}
                onChange={(e) => setForm((f) => ({ ...f, typeOrganisation: e.target.value as TypeOrganisation }))}
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-surface text-content
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {TYPES_ORGANISATION.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-2">Référentiel comptable</label>
              <div className="space-y-2">
                {(['SYSCOHADA', 'SYCEBNL'] as ReferentielComptable[]).map((ref) => (
                  <label
                    key={ref}
                    className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      form.referentielComptable === ref
                        ? 'border-primary bg-primary-light'
                        : 'border-border hover:border-primary'
                    }`}
                  >
                    <input
                      type="radio"
                      name="referentiel"
                      value={ref}
                      checked={form.referentielComptable === ref}
                      onChange={() => setForm((f) => ({ ...f, referentielComptable: ref }))}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-content">{ref}</p>
                      <p className="text-xs text-content-subtle">
                        {ref === 'SYSCOHADA'
                          ? 'Entreprises commerciales — zone OHADA'
                          : 'Entités à but non lucratif (ONG, associations, projets) — zone OHADA'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-content-muted mb-1">Description (optionnelle)</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                className="w-full border border-border rounded-lg px-3 py-2.5 text-sm bg-surface text-content resize-none
                           focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>

            {mutation.isError && (
              <p className="text-sm text-danger bg-danger-light border border-danger rounded-lg px-3 py-2">
                Une erreur est survenue. Veuillez réessayer.
              </p>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
                className="px-5 py-2.5 bg-primary text-content-inverted text-sm font-semibold rounded-lg
                           hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {mutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                onClick={annuler}
                className="px-4 py-2.5 text-sm font-medium text-content-muted bg-surface-overlay
                           hover:bg-surface-raised rounded-lg transition-colors border border-border"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Raccourci vers les projets ── */}
      <div className="bg-surface border border-border rounded-xl p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary-light flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-primary-text" />
          </div>
          <div>
            <p className="text-sm font-semibold text-content">Projets de l'organisation</p>
            <p className="text-xs text-content-subtle">Gérer les projets et leurs affectations d'employés</p>
          </div>
        </div>
        <Link
          to="/paie/administration/projets"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-text
                     hover:text-primary hover:bg-primary-light px-3 py-2 rounded-lg transition-colors"
        >
          Voir les projets
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
